import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import '../supplier-info.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import AgGridAtom from '../../../../components/atoms/table/ag-grid';
import { formatCurrencyToLocale, getFormattedDate } from '../../../../utils/method';

/**
 * A reusable component for displaying ticket-based data in a collapsible section
 * @param {Object} props Component props
 * @param {string} props.title The title to display in the header
 * @param {Array} props.data The array of ticket data items
 * @param {string} props.totalField The field name to use for total calculation
 * @param {Array} props.columnDefinitions Custom column definitions
 * @param {Object} props.totalStyle Style object for the total value display
 * @param {string} props.currency Currency symbol to display (default: €)
 * @param {boolean} props.initialCollapsed Whether the section is initially collapsed
 * @param {string} props.amountFieldHeaderClass Header class for the amount column
 * @param {string} props.amountFieldCellStyle Cell style for the amount column
 * @param {number} props.gridHeight Height of the grid (default: 220px)
 */
const TicketDataSection = ({
    title,
    data = [],
    sampleData = [],
    totalAmount = 0,
    columnDefinitions = null,
    totalStyle = {},
    initialCollapsed = true,
    amountFieldCellStyle = { textAlign: 'right' },
    gridHeight = '220px'
}) => {
    const { t } = useTranslation();
    const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);

    const toggleCollapse = () => setIsCollapsed(!isCollapsed);

    // Use provided data or fallback to sample data
    const displayData = data?.length ? data : sampleData;

    // Default column definitions if none provided
    const defaultColumnDefs = useMemo(() => [
        {
            headerName: t('supplier-info.tickets-section.nr'),
            field: 'nr',
            valueGetter: (params) => `${params.node.rowIndex + 1}.`,
            maxWidth: 90,
        },
        {
            headerName: t('supplier-info.tickets-section.code'),
            field: 'displayCode',
            maxWidth: 90,
        },
        {
            headerName: t('supplier-info.tickets-section.name'),
            field: 'name',
        },
        {
            headerName: t('supplier-info.tickets-section.description'),
            field: 'description',
        },
        {
            headerName: t('supplier-info.tickets-section.status.title'),
            field: 'status',
            valueFormatter: params => {
                const status = params.value;
                return t(`supplier-info.tickets-section.status.${status}`);
            }
        },
        {
            headerName: t('supplier-info.tickets-section.reported-date'),
            field: 'created_at',
            maxWidth: 130,
            valueFormatter: params => params.value ? getFormattedDate(params.value) : '',
        },
        {
            headerName: t('supplier-info.tickets-section.caused-by'),
            field: 'causedBy',
            width: 120
        },
        {
            headerName: t('supplier-info.tickets-section.amount'),
            field: 'displayAmount',
            width: 180,
            cellStyle: amountFieldCellStyle,
            type: "numericColumn",
            valueFormatter: params => {
                return formatCurrencyToLocale(params.value || 0);
            },
        }
    ], [t, amountFieldCellStyle]);

    // Use provided column definitions or default ones
    const columnDefs = columnDefinitions || defaultColumnDefs;

    // Add row style function to highlight closed tickets
    const getRowStyle = params => {
        if (params.data && params.data.status === 'CLOSED') {
            return { backgroundColor: 'rgba(220, 53, 69, 0.2)' }; // Light red background
        }
        return null;
    };

    return (
        <Card className="mb-3">
            <Card.Header
                onClick={toggleCollapse}
                className="bg-light si-clickable-header"
            >
                <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-bold">{title}</span>
                    <div className="d-flex align-items-center">
                        <div className="me-3">
                            <b style={totalStyle}>{t('supplier-info.total')}: {formatCurrencyToLocale(totalAmount)}</b>
                        </div>
                        <FontAwesomeIcon
                            icon={isCollapsed ? faChevronDown : faChevronUp}
                        />
                    </div>
                </div>
            </Card.Header>

            {!isCollapsed && (
                <Card.Body className="si-card-body-grid">
                    <div style={{ height: gridHeight }}>
                        <AgGridAtom
                            columnDefs={columnDefs}
                            rowData={displayData}
                            gridHeight={gridHeight}
                            getRowStyle={getRowStyle}
                            defaultColDef={{
                                flex: 1,
                                sortable: false,
                                wrapText: true,
                                autoHeight: true,
                            }}
                        />
                    </div>
                </Card.Body>
            )}
        </Card>
    );
};

export default TicketDataSection;
